import {
  browserTracingIntegration,
  type ErrorEvent,
  type EventHint,
  init as initSentry,
  replayIntegration,
} from '@sentry/react';

import { GLOBAL_ERRORS } from '@didaskill/domain/error-codes';
import { isResponseError } from '@didaskill/types/web';
import type { ApiErrorResponse } from '@didaskill/utils/express';

import { loadSentryConfig } from './config';

interface ResponseErrorWithStatus extends Error {
  status?: number;
  response?: {
    body?: ApiErrorResponse;
  };
}

const DEFAULT_REPLAYS_SESSION_SAMPLE_RATE = 0.05;
const DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE = 0.5;
const DEFAULT_TRACE_SAMPLE_RATE = 0.1;
const DEFAULT_IGNORED_HTTP_PATHS = ['/ping'];
const config = loadSentryConfig();

type SentryInitOptions = NonNullable<Parameters<typeof initSentry>[0]>;
type TracesSamplerContext = Parameters<NonNullable<SentryInitOptions['tracesSampler']>>[0];

initSentry({
  dsn: config?.dsn,
  environment: config?.env,
  enabled: config?.enabled,
  sendDefaultPii: true,
  replaysSessionSampleRate:
    config?.env === 'prod'
      ? (config?.replaysSessionSampleRate ?? DEFAULT_REPLAYS_SESSION_SAMPLE_RATE)
      : 0.0,
  replaysOnErrorSampleRate:
    config?.env === 'prod'
      ? (config?.replaysOnErrorSampleRate ?? DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE)
      : 0.0,
  integrations: [
    browserTracingIntegration({
      ignoreResourceSpans: [
        'resource.css',
        'resource.script',
        'resource.audio',
        'resource.img',
        'resource.link',
        'resource.other',
      ],
    }),
    replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  tracesSampler: (samplingContext: TracesSamplerContext) => {
    const { inheritOrSampleWith, name } = samplingContext;

    if (DEFAULT_IGNORED_HTTP_PATHS.some((path) => name.includes(path))) {
      return 0;
    }

    return inheritOrSampleWith(config?.traceSampleRate ?? DEFAULT_TRACE_SAMPLE_RATE);
  },
  beforeSend(event: ErrorEvent, hint: EventHint) {
    const exception = hint.originalException;

    if (exception instanceof Error) {
      const error: Error = exception;

      if (isResponseError(error)) {
        const responseError = error as ResponseErrorWithStatus;
        if (
          responseError.status === 401 &&
          responseError?.response?.body &&
          responseError.response.body.message === GLOBAL_ERRORS.USER_NOT_AUTHENTICATED.MESSAGE
        ) {
          return null;
        }
      }
    }

    return event;
  },
});
